import {
  type BRANCH_ENVIRONMENT_DEVELOPMENT_ENUM,
  BRANCH_ENVIRONMENT_PRODUCTION_ENUM,
  type BRANCH_ENVIRONMENT_SCRATCH_ENUM
} from '@takeshape/branches';
import type { FormsConfig, ProjectSchemaJSON, QueryJSON, ShapeJSON } from '@takeshape/schema';
import type { ProjectSchemaCompat, ProjectSchemaRecordType } from 'app/compat/types';
import { schemaRecordToCompatSchema } from 'app/compat/utils';
import { mutateAdmin, queryAdmin } from 'app/graphql-client';
import type { BranchQueryArgs } from './types';

export const getAllServiceLayersQuery = queryAdmin(/* GraphQL */ `
  query ($environment: TSSchemaBranchEnvironment!, $branchName: String, $userId: String, $projectId: String!) {
    getAllServiceLayers(environment: $environment, branchName: $branchName, userId: $userId, projectId: $projectId) {
      servicesHash
      layers {
        id
        status
        schema
      }
    }
  }
`);

export type ProductionBranch = {
  environment: typeof BRANCH_ENVIRONMENT_PRODUCTION_ENUM;
};

export type DevelopmentBranch = {
  environment: typeof BRANCH_ENVIRONMENT_DEVELOPMENT_ENUM;
  branchName: string;
};

export type ScratchBranch = {
  environment: typeof BRANCH_ENVIRONMENT_SCRATCH_ENUM;
  branchName?: string;
  userId: string;
};

export type TargetProductionBranch = {
  version: number;
} & ProductionBranch;

export type TargetDevelopmentBranch = {
  version: number;
} & DevelopmentBranch;

export type MergedBranchResponse = {
  mergedBranch: {
    latestVersion: {
      schema: ProjectSchemaCompat;
    };
  };
};

export async function getBranchListQuery(): Promise<{
  branches: DevelopmentBranch[] | null;
}> {
  const res = await queryAdmin(/* GraphQL */ `
    query ($projectId: String) {
      getSchemaBranchList(environments: [DEVELOPMENT], first: 100, projectId: $projectId) {
        branches: nodes {
          environment
          branchName
        }
      }
    }
  `)();
  return res.getSchemaBranchList;
}

export type MergeBranchMutationInput = {
  head: DevelopmentBranch;
  base: ProductionBranch;
  target: TargetProductionBranch | TargetDevelopmentBranch;
  dryRun?: boolean;
  deleteMergedHead?: boolean;
};

export const isProductionTargetBranch = (
  target: TargetProductionBranch | TargetDevelopmentBranch
): target is TargetProductionBranch => {
  return target.environment === BRANCH_ENVIRONMENT_PRODUCTION_ENUM;
};

export const MERGE_BRANCH_MUTATION = /* GraphQL */ `
  mutation ($input: TSMergeSchemaBranchInput!) {
    mergeSchemaBranch(
      input: $input
    ) {
      mergedBranch {
        latestVersion {
          schema
        }
      }
    }
  }
`;

export async function mergeBranchMutation(input: MergeBranchMutationInput): Promise<MergedBranchResponse> {
  const res = await mutateAdmin(MERGE_BRANCH_MUTATION)({ input });
  return res.mergeSchemaBranch;
}

export const CREATE_DEVELOPMENT_BRANCH_MUTATION = /* GraphQL */ `
  mutation ($name: String!) {
    createSchemaBranch(input: {environment: DEVELOPMENT, branchName: $name}) {
      branch {
        environment
        branchName
      }
    }
  }
`;

export async function createBranchMutation(name: string): Promise<{ branch: DevelopmentBranch }> {
  const res = await mutateAdmin(CREATE_DEVELOPMENT_BRANCH_MUTATION)({ name });
  return res.createSchemaBranch;
}

export const CREATE_SCRATCH_BRANCH_MUTATION = /* GraphQL */ `
  mutation ($projectId: String!, $branchName: String, $userId: String!) {
    createSchemaBranch(input: {projectId: $projectId, environment: SCRATCH, branchName: $branchName, userId: $userId}) {
      branch {
        environment
        branchName
        userId
      }
    }
  }
`;

export async function createScratchBranchMutation(
  projectId: string,
  userId: string,
  branchName?: string
): Promise<{ branch: ScratchBranch }> {
  const res = await mutateAdmin(CREATE_SCRATCH_BRANCH_MUTATION)({ projectId, branchName, userId });
  return res.createSchemaBranch;
}

export const DELETE_BRANCH_MUTATION = /* GraphQL */ `
  mutation ($name: String!) {
    deleteSchemaBranch(input: {environment: DEVELOPMENT, branchName: $name}) {
      deletedBranch {
        environment
        branchName
      }
    }
  }
`;

export async function deleteBranchMutation(name: string): Promise<{ deletedBranch: DevelopmentBranch }> {
  const res = await mutateAdmin(DELETE_BRANCH_MUTATION)({ name });
  return res.deleteSchemaBranch;
}

export type GetSchemaBranchArgs = BranchQueryArgs & {
  schemaType: ProjectSchemaRecordType;
};

export const SCHEMA_FAILED_TO_LOAD = 'Failed to load schema';

export async function getSchemaQuery(args: GetSchemaBranchArgs): Promise<ProjectSchemaCompat> {
  const res = await queryAdmin(/* GraphQL */ `
    query (
      $schemaType: TSSchemaType!
      $projectId: String!
      $environment: TSSchemaBranchEnvironment!
      $branchName: String
      $userId: String
    ) {
      schemaRecord: getSchema(
        schemaType: $schemaType
        projectId: $projectId
        environment: $environment
        branchName: $branchName
        userId: $userId
      )
    }
  `)(args);

  if (!res.schemaRecord) {
    throw new Error(SCHEMA_FAILED_TO_LOAD);
  }

  return schemaRecordToCompatSchema(res.schemaRecord);
}

export type SchemaUpdate = {
  version: number;
  shapes: Record<string, ShapeJSON | null>;
  forms: Record<string, FormsConfig | null>;
  queries: Record<string, QueryJSON | null>;
  mutations: Record<string, QueryJSON | null>;
};

export type UpdateSchemaMutationArgs = SchemaUpdate & BranchQueryArgs;

export const UPDATE_SCHEMA_MUTATION = /* GraphQL */ `
  mutation ($input: TSUpdateSchemaInput!) {
    schemaRecord: updateSchema(input: $input)
  }
`;

export async function updateSchemaMutation(update: UpdateSchemaMutationArgs): Promise<ProjectSchemaCompat> {
  const { version, projectId, environment, branchName, userId, ...schema } = update;

  const input = {
    version,
    projectId,
    environment,
    branchName,
    userId,
    schema
  };

  const res = await mutateAdmin(UPDATE_SCHEMA_MUTATION)({ input });

  return schemaRecordToCompatSchema(res.schemaRecord);
}

export type UpdateOrCreateSchemaMutationArgs = BranchQueryArgs & {
  schemaType: ProjectSchemaRecordType;
  version: number;
  schema: ProjectSchemaJSON;
};

export const UPDATE_OR_CREATE_SCHEMA_MUTATION = /* GraphQL */ `
  mutation ($input: TSUpdateOrCreateSchemaInput!) {
    schemaRecord: updateOrCreateSchema(input: $input)
  }
`;

export async function updateOrCreateSchemaMutation(
  input: UpdateOrCreateSchemaMutationArgs
): Promise<ProjectSchemaCompat> {
  const res = await mutateAdmin(UPDATE_OR_CREATE_SCHEMA_MUTATION)({ input });

  return schemaRecordToCompatSchema(res.schemaRecord);
}
